// UI
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import CommentsButton from '@/components/ui/CommentsButton'

// Service Case sections
import ServiceCaseStatus from '@/components/service_cases/sections/ServiceCaseStatus'
import ServiceCaseHandlingLocation from '@/components/service_cases/sections/ServiceCaseHandlingLocation'
import ServiceCaseCustomer from '@/components/service_cases/sections/ServiceCaseCustomer'
import ServiceCaseAddress from '@/components/service_cases/sections/ServiceCaseAddress'
import ServiceCaseShipments from '@/components/service_cases/sections/ServiceCaseShipments'
import ServiceCaseProduct from '@/components/service_cases/sections/ServiceCaseProduct'
import ServiceCaseWarranty from '@/components/service_cases/sections/ServiceCaseWarranty'
import ServiceCaseReceptionItems from '@/components/service_cases/sections/ServiceCaseReceptionItems'
import ServiceCaseNote from '@/components/service_cases/sections/ServiceCaseNote'
import ServiceCaseInterventions from '@/components/service_cases/sections/ServiceCaseInterventions'
import ServiceCaseReasons from '@/components/service_cases/sections/ServiceCaseReasons'
import ServiceCaseGorgias from '@/components/service_cases/sections/ServiceCaseGorgias'
import ServiceCaseOrder from '@/components/service_cases/sections/ServiceCaseOrder'
import ServiceCaseActions from '@/components/service_cases/sections/ServiceCaseActions'
import ServiceCaseParallelActions from '@/components/service_cases/sections/ServiceCaseParallelActions'
import ServiceCaseStock from '@/components/service_cases/sections/ServiceCaseStock'
import GorgiasAiSummary from '@/components/gorgias/GorgiasAiSummary'
import Attachments from '@/components/ui/Attachments'

// Service Case components
import ServiceCaseHistory from '@/components/service_cases/ServiceCaseHistory'

// Mantine
import { ActionIcon, Menu, Skeleton, Button, Modal, Tooltip, Indicator } from '@mantine/core'

// Icons
import { DotsVertical, Star01, XClose, Attachment01 } from '@untitled-ui/icons-react'

// Lib
import { serviceCaseStatuses, serviceCaseTypes } from '@/lib/serviceCaseStatuses'
import classNames from 'classnames'

// Service Case UI
import StatusPickerList from '@/components/service_cases/ui/StatusPickerList'
import CreateFulfillmentOrderModal from '@/components/service_cases/CreateFulfillmentOrderModal'

// Hook
import useServiceCase from '@/hooks/useServiceCase'
import useUserLocation from '@/hooks/useUserLocation'
import { useAxiosApi } from '@/hooks/useAxiosApi'

import { useState } from 'react'
import { notifications } from '@mantine/notifications'

// Wrapper component for modal vs full page
const Wrapper = ({
    serviceCaseId,
    children,
    withScrollableScreen,
    breadcrumbs,
    rightSection = null,
    onClose = null,
    serviceCase,
}) => {
    if (withScrollableScreen) {
        return (
            <ScrollableScreen
                breadcrumbs={breadcrumbs}
                rightSection={rightSection}
                commentableType="service_case"
                commentableId={serviceCaseId}
            >
                <div className="flex flex-col gap-5 w-full p-5 max-w-[900px] mx-auto">
                    {children}
                </div>
            </ScrollableScreen>
        )
    }

    return (
        <div className="flex flex-col gap-5 relative">
            <div className="sticky top-0 p-5 left-0 right-0 bg-white z-10 border-b shadow-xs">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        {serviceCase?.is_priority && (
                            <Star01
                                className="text-red-500 fill-red-500"
                                width={18}
                                height={18}
                            />
                        )}
                        <span className="font-medium text-lg font-mono">
                            {serviceCase?.display_name}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <CommentsButton
                            commentableType="service_case"
                            commentableId={serviceCaseId}
                        />

                        {rightSection}

                        {onClose && (
                            <ActionIcon
                                variant="default"
                                size={36}
                                onClick={onClose}
                            >
                                <XClose width={16} height={16} />
                            </ActionIcon>
                        )}
                    </div>
                </div>
            </div>

            <div className="px-5 pb-5 flex flex-col gap-5">{children}</div>
        </div>
    )
}

export default function ShowServiceCase({
    serviceCaseId,
    withScrollableScreen = true,
    onClose = null,
    onMutate = null,
}) {
    // Hook
    const { serviceCase, isLoading, reload, updateServiceCase, performAction } =
        useServiceCase({ serviceCaseId, onMutate })
    const { userPrinters } = useUserLocation()
    const { requestPost } = useAxiosApi()
    const productLabelPrinter = userPrinters.product_label

    // State (must be before any early return)
    const [typeModalOpened, setTypeModalOpened] = useState(false)
    const [selectedType, setSelectedType] = useState(null)
    const [selectedTypeStatus, setSelectedTypeStatus] = useState(null)
    const [isTypeSubmitting, setIsTypeSubmitting] = useState(false)
    const [foModalOpened, setFoModalOpened] = useState(false)
    const [attachmentsOpened, setAttachmentsOpened] = useState(false)

    // Breadcrumbs (only used in full page mode)
    const breadcrumbs = [
        {
            label: 'Service après-vente',
            href: '/service-cases?is_archived=0',
        },
        {
            label: isLoading
                ? 'Chargement...'
                : serviceCase?.display_name,
        },
    ]

    // Loading state
    if (isLoading || !serviceCase) {
        return (
            <Wrapper
                serviceCaseId={serviceCaseId}
                withScrollableScreen={withScrollableScreen}
                breadcrumbs={breadcrumbs}
                onClose={onClose}
            >
                <Skeleton height={150} radius="xl" />
                <Skeleton height={400} radius="xl" />
                <Skeleton height={150} radius="xl" />
                <Skeleton height={150} radius="xl" />
                <Skeleton height={250} radius="xl" />
                <Skeleton height={250} radius="xl" />
                <Skeleton height={150} radius="xl" />
                <Skeleton height={150} radius="xl" />
            </Wrapper>
        )
    }

    // --- Menu actions ---
    const handleArchive = () => {
        updateServiceCase({ is_archived: serviceCase.is_archived ? '0' : '1' })
    }

    const handlePriority = () => {
        updateServiceCase({ is_priority: !serviceCase.is_priority })
    }

    const handleFinish = () => {
        performAction('finish')
    }

    const handleToggleFlag = (flag) => {
        performAction('toggle_flag', { flag })
    }

    const handlePrintLabel = async () => {
        try {
            await requestPost(`/service-cases/${serviceCase.id}/print-label`, {
                printnode_ids: productLabelPrinter.printnode_ids,
            })
            notifications.show({
                title: 'Impression lancée',
                message: `Envoyé sur ${productLabelPrinter.name}`,
                color: 'green',
            })
        } catch (e) {
            notifications.show({
                title: "Erreur d'impression",
                message: e.response?.data?.error_message || 'Erreur inconnue',
                color: 'red',
            })
        }
    }

    const handleOpenTypeModal = () => {
        setSelectedType(serviceCase.type)
        setSelectedTypeStatus(serviceCase.status)
        setTypeModalOpened(true)
    }

    const handleTypeSelect = (typeKey) => {
        setSelectedType(typeKey)
        const statusConfig = serviceCaseStatuses[selectedTypeStatus]
        if (statusConfig && !statusConfig.validTypes.includes(typeKey)) {
            setSelectedTypeStatus(null)
        }
    }

    const handleConfirmTypeChange = async () => {
        if (!selectedType || !selectedTypeStatus) return
        setIsTypeSubmitting(true)
        const updates = {}
        if (selectedType !== serviceCase.type) updates.type = selectedType
        if (selectedTypeStatus !== serviceCase.status) updates.status = selectedTypeStatus
        if (Object.keys(updates).length > 0) {
            await updateServiceCase(updates)
        }
        setIsTypeSubmitting(false)
        setTypeModalOpened(false)
    }

    const compatibleStatuses = selectedType
        ? Object.entries(serviceCaseStatuses).filter(
              ([, config]) => !config.virtual && config.validTypes?.includes(selectedType)
          )
        : []

    const hasTypeChanges =
        selectedType !== serviceCase.type ||
        selectedTypeStatus !== serviceCase.status

    // Right section (actions menu)
    const rightSection = (
        <div className="flex gap-2 items-center">
            <ServiceCaseStock serviceCase={serviceCase} onMutate={reload} />
            <ServiceCaseShipments serviceCase={serviceCase} />
            <ServiceCaseGorgias serviceCase={serviceCase} />
            <ServiceCaseHistory serviceCaseId={serviceCase.id} />

            <Tooltip label="Pièces jointes">
                <Indicator label={serviceCase.attachments?.length || 0} size={16} disabled={!serviceCase.attachments?.length} color="dark">
                    <ActionIcon variant="default" size={36} onClick={() => setAttachmentsOpened(true)}>
                        <Attachment01 width={16} height={16} />
                    </ActionIcon>
                </Indicator>
            </Tooltip>

            <Menu position="bottom-end">
                <Menu.Target>
                    <ActionIcon variant="default" size={36}>
                        <DotsVertical width={16} height={16} />
                    </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                    {productLabelPrinter && (
                        <Menu.Item onClick={handlePrintLabel}>
                            Imprimer l'étiquette SAV
                        </Menu.Item>
                    )}
                    <Menu.Item onClick={handleArchive}>
                        {serviceCase.is_archived
                            ? 'Désarchiver'
                            : 'Archiver'}
                    </Menu.Item>
                    <Menu.Item onClick={handlePriority}>
                        {serviceCase.is_priority
                            ? 'Retirer la priorité'
                            : 'Marquer comme prioritaire'}
                    </Menu.Item>
                    <Menu.Item onClick={handleFinish}>
                        Terminer manuellement
                    </Menu.Item>

                    {(serviceCase.type === 'swap_same' || serviceCase.type === 'return') && (
                        <>
                            <Menu.Divider />
                            <Menu.Item onClick={() => handleToggleFlag('diagnosis_cleared_at')}>
                                {serviceCase.diagnosis_cleared_at
                                    ? serviceCase.type === 'swap_same'
                                        ? "Annuler l'autorisation d'échange"
                                        : 'Annuler l\'autorisation de remboursement'
                                    : serviceCase.type === 'swap_same'
                                        ? "Autoriser l'échange"
                                        : 'Autoriser le remboursement'}
                            </Menu.Item>
                            {serviceCase.type === 'swap_same' && (
                                <Menu.Item onClick={() => handleToggleFlag('swapped_at')}>
                                    {serviceCase.swapped_at
                                        ? "Annuler le marquage d'échange"
                                        : 'Marquer comme échangé'}
                                </Menu.Item>
                            )}
                            {serviceCase.type === 'return' && (
                                <Menu.Item onClick={() => handleToggleFlag('refunded_at')}>
                                    {serviceCase.refunded_at
                                        ? 'Annuler le marquage de remboursement'
                                        : 'Marquer comme remboursé'}
                                </Menu.Item>
                            )}
                        </>
                    )}

                    <Menu.Divider />
                    <Menu.Item onClick={() => setFoModalOpened(true)}>
                        Créer un bon de préparation
                    </Menu.Item>
                    <Menu.Item onClick={handleOpenTypeModal}>
                        Modifier le type ou l'étape
                    </Menu.Item>

                    <Menu.Divider />
                    <Menu.Item onClick={() => window.open(serviceCase.tracking_url, '_blank')}>
                        Page de suivi client
                    </Menu.Item>
                    {serviceCase.report_pdf_urls && (
                        <>
                            <Menu.Item onClick={() => window.open(serviceCase.report_pdf_urls.fr, '_blank')}>
                                Rapport d'intervention (FR)
                            </Menu.Item>
                            <Menu.Item onClick={() => window.open(serviceCase.report_pdf_urls.en, '_blank')}>
                                Rapport d'intervention (EN)
                            </Menu.Item>
                        </>
                    )}
                </Menu.Dropdown>
            </Menu>
        </div>
    )

    return (
        <>
            <Wrapper
                serviceCaseId={serviceCaseId}
                withScrollableScreen={withScrollableScreen}
                breadcrumbs={breadcrumbs}
                rightSection={rightSection}
                onClose={onClose}
                serviceCase={serviceCase}
            >
                <ServiceCaseStatus serviceCase={serviceCase} />
                <GorgiasAiSummary
                    ai={{
                        abstract: serviceCase.gorgias_ticket?.ai_abstract_fr,
                        sentiment: serviceCase.gorgias_ticket?.ai_sentiment,
                        frustration: serviceCase.gorgias_ticket?.ai_frustration,
                        satisfaction: serviceCase.gorgias_ticket?.ai_satisfaction,
                        resolution: serviceCase.gorgias_ticket?.ai_resolution,
                    }}
                />
                {!serviceCase.is_archived && (
                    <ServiceCaseActions serviceCase={serviceCase} performAction={performAction} onPrintLabel={productLabelPrinter ? handlePrintLabel : null} onMutate={reload} />
                )}
                <ServiceCaseParallelActions serviceCase={serviceCase} performAction={performAction} reload={reload} />
                <ServiceCaseHandlingLocation serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseProduct serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseOrder serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseWarranty serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseReceptionItems serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseReasons serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseInterventions serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseCustomer serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseAddress serviceCase={serviceCase} updateServiceCase={updateServiceCase} />
                <ServiceCaseNote serviceCase={serviceCase} />
            </Wrapper>

            <CreateFulfillmentOrderModal
                serviceCase={serviceCase}
                opened={foModalOpened}
                onClose={() => setFoModalOpened(false)}
                onSuccess={reload}
            />

            <Modal
                opened={typeModalOpened}
                onClose={() => setTypeModalOpened(false)}
                title="Modifier le type ou l'étape"
                centered
            >
                <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-gray-500">Type</span>
                        {Object.entries(serviceCaseTypes).map(
                            ([key, { label, icon: Icon }]) => (
                                <div
                                    key={key}
                                    className={classNames(
                                        'border rounded-xl p-3 cursor-pointer transition-colors',
                                        {
                                            'border-blue-500 bg-blue-50 text-blue-600':
                                                key === selectedType,
                                            'border-gray-200 hover:bg-gray-50':
                                                key !== selectedType,
                                        }
                                    )}
                                    onClick={() => handleTypeSelect(key)}
                                >
                                    <span className="flex items-center gap-1.5">
                                        {Icon && <Icon width={16} height={16} />}
                                        {label}
                                    </span>
                                </div>
                            )
                        )}
                    </div>

                    {selectedType && (
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium text-gray-500">Étape</span>
                            <StatusPickerList
                                statuses={compatibleStatuses}
                                selectedKey={selectedTypeStatus}
                                onSelect={setSelectedTypeStatus}
                            />
                        </div>
                    )}

                    <Button
                        fullWidth
                        onClick={handleConfirmTypeChange}
                        loading={isTypeSubmitting}
                        disabled={!selectedType || !selectedTypeStatus || !hasTypeChanges}
                    >
                        Confirmer
                    </Button>
                </div>
            </Modal>

            <Modal
                opened={attachmentsOpened}
                onClose={() => setAttachmentsOpened(false)}
                title="Pièces jointes"
                size="lg"
                centered
            >
                <Attachments
                    model_id={serviceCase.id}
                    model_type="service_case"
                    attachments={serviceCase.attachments || []}
                    refetchModelData={reload}
                />
            </Modal>
        </>
    )
}
